import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
  ReactNode,
  useCallback,
} from 'react';
import { AxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { showSnackbar } from '@/shared/hooks/useSnackbar';
import i18n from '@/shared/i18n';
import {
  getDeviceTrustToken,
  setDeviceTrustToken,
} from '@/modules/identity/utils/deviceTrust';
import { authAPI } from '@/modules/identity/api/auth.api';
import { profileAPI } from '@/modules/identity/api/profile.api';
import { logoutSession } from '@/modules/identity/api/session.api';
import type { OrgEntitlements } from '@/shared/billing/planCatalog';
import type { TreatmentRecordingMode } from '@/shared/config/treatmentRecordingMode';

export interface AuthStaffSummary {
  id: string;
  staffCode?: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  department?: string | { id?: string; code?: string; name?: string };
  designation?: string;
  status?: string;
  isDoctor?: boolean;
  isAssistant?: boolean;
  gender?: 'male' | 'female' | 'other';
  avatarUrl?: string | null;
  primaryClinics?: string[];
}

export interface User {
  id: string;
  _id?: string;
  name: string;
  email?: string;
  username?: string;
  role: string;
  /** From GET /auth/me. Null when the account has no permission group. */
  permissionGroupName?: string | null;
  organization?: string;
  permissions?: string[];
  staffId?: string | null;
  staff?: AuthStaffSummary | null;
  gender?: 'male' | 'female' | 'other';
  /** Relative `/api` path from /auth/me (staff avatar). */
  avatar?: string | null;
  forcePasswordChange?: boolean;
  mustEnrollTwoFactor?: boolean;
  passwordPolicy?: {
    passwordExpiryDays: number;
    passwordMinLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
  };
  idleLogoutMinutes?: number;
  entitlements?: OrgEntitlements;
  /** Code-level visit recording switch from GET /auth/me. */
  treatmentRecordingMode?: TreatmentRecordingMode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<any>;
  completeTwoFactorLogin: (
    tempToken: string,
    code: string,
    options?: { rememberDevice?: boolean; username?: string }
  ) => Promise<any>;
  logout: () => void;
  loadUser: (options?: { silent?: boolean }) => Promise<void>;
  updatePassword: (passwordData: any) => Promise<any>;
  forgotPassword: (username: string) => Promise<any>;
  resetPassword: (password: string, resetToken: string) => Promise<any>;
  clearAuthData: () => void;
}

interface DecodedToken {
  id: string;
  exp: number;
  role?: string;
  organization?: string;
}

const LOGIN_ERROR_CODES: Record<string, string> = {
  INVALID_CREDENTIALS: 'auth:errors.invalidCredentials',
  ACCOUNT_LOCKED: 'auth:errors.accountLocked',
  ACCOUNT_INACTIVE: 'auth:errors.accountInactive',
  ORG_INACTIVE: 'auth:errors.orgInactive',
  TOO_MANY_REQUESTS: 'auth:errors.tooManyRequests',
  INVALID_2FA_CODE: 'auth:errors.invalid2faCode',
  INVALID_2FA_CHALLENGE: 'auth:errors.invalid2faChallenge',
  IP_NOT_ALLOWED: 'auth:errors.ipNotAllowed',
};

function mapLoginErrorMessage(error: AxiosError): string {
  const data = error.response?.data as { code?: string; message?: string } | undefined;
  const code = data?.code;
  if (code && LOGIN_ERROR_CODES[code]) {
    return i18n.t(LOGIN_ERROR_CODES[code] as 'auth:errors.generic');
  }
  if (error.response?.status === 423) {
    return i18n.t('auth:errors.accountLocked');
  }
  if (error.response?.status === 429) {
    return i18n.t('auth:errors.tooManyRequests');
  }
  if (!error.response) {
    return i18n.t('auth:errors.generic');
  }
  return i18n.t('auth:errors.generic');
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  /** Shown once `/auth/me` finishes, together with the route change — not on the login form. */
  const pendingLoginSuccessRef = useRef<string | null>(null);

  const logout = useCallback((): void => {
    const finishLocalLogout = () => {
      localStorage.removeItem('token');
      setToken(null);
      setIsAuthenticated(false);
      setUser(null);
      setError(null);
      showSnackbar(i18n.t('auth:loggedOut'), 'info');
    };

    const run = async () => {
      try {
        if (localStorage.getItem('token')) {
          await logoutSession();
        }
      } catch (err) {
        console.error('Logout API error:', err);
      } finally {
        finishLocalLogout();
      }
    };

    void run();
  }, []);

  const loadUser = useCallback(async (options?: { silent?: boolean }): Promise<void> => {
    const silent = Boolean(options?.silent);
    try {
      if (!silent) {
        setLoading(true);
      }

      const data = await profileAPI.getMe();
      setUser({
        ...data,
        id: data.id || data._id || '',
        staffId: data.staffId ?? null,
        staff: data.staff
          ? {
              id: data.staff.id,
              staffCode: data.staff.staffCode,
              firstName: data.staff.firstName || '',
              lastName: data.staff.lastName || '',
              email: data.staff.email,
              phone: data.staff.phone,
              department: data.staff.department,
              designation: data.staff.designation,
              status: data.staff.status,
              isDoctor: Boolean(data.staff.isDoctor),
              isAssistant: Boolean(data.staff.isAssistant),
              gender: data.staff.gender,
              avatarUrl: data.staff.avatarUrl,
              primaryClinics: data.staff.primaryClinics,
            }
          : null,
        avatar: data.avatar ?? null,
      });
      setIsAuthenticated(true);
      setError(null);
      const loginMessage = pendingLoginSuccessRef.current;
      pendingLoginSuccessRef.current = null;
      if (loginMessage) showSnackbar(loginMessage, 'success');
    } catch (err) {
      pendingLoginSuccessRef.current = null;
      const axiosError = err as AxiosError;
      console.error('Load user error:', axiosError);
      const message =
        (axiosError.response?.data as { message?: string })?.message ||
        i18n.t('auth:loadUserError');
      setError(message);
      // 429 is transient (global rate limit) — keep session so dev/tabs do not force re-login.
      if (axiosError.response?.status === 429) {
        return;
      }
      setToken(null);
      localStorage.removeItem('token');
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (token) {
      loadUser();
    } else {
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
    }
  }, [token, loadUser]);

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode<DecodedToken>(token);
        const currentTime = Date.now() / 1000;

        if (decoded.exp < currentTime) {
          logout();
          showSnackbar(i18n.t('auth:sessionExpired'), 'error');
          return;
        }

        if (!decoded.id) {
          console.warn('Token has invalid structure, logging out');
          logout();
          showSnackbar(i18n.t('auth:sessionInvalid'), 'error');
          return;
        }
      } catch (err) {
        console.error('Token validation failed:', err);
        logout();
        showSnackbar(i18n.t('auth:sessionValidationFailed'), 'error');
      }
    }
  }, [token, logout]);

  const clearAuthData = (): void => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
    localStorage.removeItem('token');
    sessionStorage.clear();
  };

  const login = async (username: string, password: string): Promise<any> => {
    try {
      setLoading(true);
      setError(null);

      const deviceTrustToken = getDeviceTrustToken(username);
      const res = await authAPI.login({
        username,
        password,
        ...(deviceTrustToken ? { deviceTrustToken } : {}),
      });
      const data = res.data;

      if (data.requires2FA && data.tempToken) {
        setLoading(false);
        return res;
      }

      const rawToken = data.token;
      localStorage.setItem('token', rawToken);
      setToken(rawToken);

      const displayName = data.user?.name || username;
      pendingLoginSuccessRef.current = i18n.t('auth:loginSuccess', { name: displayName });
      return res;
    } catch (err) {
      const axiosError = err as AxiosError;
      console.error('Login error:', axiosError);
      const errorMessage = mapLoginErrorMessage(axiosError);
      setError(errorMessage);
      setLoading(false);
      throw axiosError;
    }
  };

  const completeTwoFactorLogin = async (
    tempToken: string,
    code: string,
    options?: { rememberDevice?: boolean; username?: string }
  ): Promise<any> => {
    try {
      setLoading(true);
      setError(null);

      const res = await authAPI.loginTwoFactor({
        tempToken,
        code,
        rememberDevice: Boolean(options?.rememberDevice),
      });
      const rawToken = res.data.token;
      const deviceTrustToken = res.data.deviceTrustToken as string | undefined;

      if (deviceTrustToken && options?.username) {
        setDeviceTrustToken(options.username, deviceTrustToken);
      }

      localStorage.setItem('token', rawToken);
      setToken(rawToken);

      const displayName = res.data.user?.name || options?.username || '';
      pendingLoginSuccessRef.current = i18n.t('auth:loginSuccess', { name: displayName });
      return res;
    } catch (err) {
      const axiosError = err as AxiosError;
      console.error('2FA login error:', axiosError);
      const errorMessage = mapLoginErrorMessage(axiosError);
      setError(errorMessage);
      setLoading(false);
      throw axiosError;
    }
  };

  const updatePassword = async (passwordData: any): Promise<any> => {
    try {
      setLoading(true);
      const res = await authAPI.updatePassword(passwordData);
      showSnackbar(i18n.t('auth:passwordUpdated'), 'success');
      return res;
    } catch (err) {
      const axiosError = err as AxiosError;
      setError((axiosError.response?.data as any)?.message || i18n.t('auth:passwordUpdateFailed'));
      showSnackbar(
        (axiosError.response?.data as any)?.message || i18n.t('auth:passwordUpdateFailed'),
        'error'
      );
      throw axiosError;
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (username: string): Promise<any> => {
    try {
      setLoading(true);
      const res = await authAPI.forgotPassword(username);
      showSnackbar(i18n.t('auth:resetEmailSent'), 'success');
      return res;
    } catch (err) {
      const axiosError = err as AxiosError;
      setError((axiosError.response?.data as any)?.message || i18n.t('auth:resetEmailFailed'));
      showSnackbar(
        (axiosError.response?.data as any)?.message || i18n.t('auth:resetEmailFailed'),
        'error'
      );
      throw axiosError;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (password: string, resetToken: string): Promise<any> => {
    try {
      setLoading(true);
      const res = await authAPI.resetPassword(password, resetToken);
      showSnackbar(i18n.t('auth:passwordReset'), 'success');
      return res;
    } catch (err) {
      const axiosError = err as AxiosError;
      setError((axiosError.response?.data as any)?.message || i18n.t('auth:passwordResetFailed'));
      showSnackbar(
        (axiosError.response?.data as any)?.message || i18n.t('auth:passwordResetFailed'),
        'error'
      );
      throw axiosError;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        loading,
        error,
        login,
        completeTwoFactorLogin,
        logout,
        loadUser,
        updatePassword,
        forgotPassword,
        resetPassword,
        clearAuthData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
